import React, { useCallback, useEffect, useRef, useState } from 'react';
import SearchablePagedDropdown from './SearchablePagedDropdown';
import { chequeBookApi } from '../../services/financeApi';

/**
 * Cheque number for a cheque payment.
 * - The bank account has cheque books and the general setting allows picking → searchable,
 *   scroll-loading list of its AVAILABLE cheques
 *   (active book first). Otherwise (default) the number is read-only: the next cheque is issued.
 *   When empty, the next cheque is filled in automatically.
 * - No cheque books for the account → plain text box, as before.
 * The server checks the number again when the payment is saved.
 *
 * Props: ledgerAccountId, value, onChange(chequeNumber), error, disabled, className (text box),
 *        autoFill (default true), menuPlacement
 */
const ChequeNumberSelect = ({
  ledgerAccountId,
  value,
  onChange,
  error = false,
  disabled = false,
  className = '',
  placeholder = 'Cheque number',
  autoFill = true,
  menuPlacement = 'auto'
}) => {
  const [hasBooks, setHasBooks] = useState(null); // null = unknown / loading
  const [selectable, setSelectable] = useState(false); // general setting "allow selecting cheque numbers"
  const [nextCheque, setNextCheque] = useState(null);
  const [info, setInfo] = useState('');
  const valueRef = useRef(value);
  valueRef.current = value;
  const prevLedgerRef = useRef(ledgerAccountId);

  // Does this account issue cheques from cheque books? (and the next one, to pre-fill)
  useEffect(() => {
    let cancelled = false;
    setInfo('');
    // Another bank account was chosen: the old cheque number belongs to the old account
    const ledgerChanged = prevLedgerRef.current !== ledgerAccountId;
    prevLedgerRef.current = ledgerAccountId;
    if (ledgerChanged && valueRef.current) {
      valueRef.current = '';
      onChange('');
    }
    if (!ledgerAccountId) { setHasBooks(false); return undefined; }
    setHasBooks(null);
    chequeBookApi.getAvailable({ ledgerAccountId, page: 1, limit: 1 })
      .then((res) => {
        if (cancelled) return;
        const d = res?.data?.data || {};
        setHasBooks(!!d.hasBooks);
        setSelectable(!!d.selectable);
        if (d.hasBooks) {
          const next = d.items?.[0];
          setNextCheque(next || null);
          setInfo(next ? `${d.total} cheque${d.total !== 1 ? 's' : ''} left · next ${next.chequeNumber} (${next.bookNumber})` : 'No unused cheques left in the cheque books of this account');
          if (autoFill && next && !valueRef.current) onChange(next.chequeNumber);
        }
      })
      .catch(() => { if (!cancelled) setHasBooks(false); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ledgerAccountId]);

  const fetchPage = useCallback(async (search, page, limit) => {
    const res = await chequeBookApi.getAvailable({ ledgerAccountId, search: search || undefined, page, limit });
    const d = res?.data?.data || {};
    return {
      items: (d.items || []).map(i => ({ ...i, _id: i.chequeNumber })),
      page: d.page || page,
      pages: d.pages || 1,
      total: d.total || 0
    };
  }, [ledgerAccountId]);

  // Cheque numbers are issued in order (general setting): read-only, the next cheque is used
  if (hasBooks && !selectable) {
    const boxCls = className || `w-full px-3 py-1.5 text-sm border rounded ${error ? 'border-red-300 bg-red-50' : 'border-gray-300'}`;
    return (
      <div>
        <div className="flex items-center gap-1.5">
          <input
            type="text"
            value={value || ''}
            readOnly
            tabIndex={-1}
            placeholder={nextCheque ? 'No cheque' : 'No cheques left'}
            title="Cheque numbers are issued in order from the cheque book"
            className={`${boxCls} bg-gray-50 font-mono cursor-not-allowed`}
          />
          {!autoFill && !disabled && (
            value ? (
              <button type="button" onClick={() => onChange('')}
                className="px-2 py-1 text-xs border border-gray-300 rounded text-gray-600 hover:bg-gray-50 whitespace-nowrap">Clear</button>
            ) : nextCheque && (
              <button type="button" onClick={() => onChange(nextCheque.chequeNumber)}
                className="px-2 py-1 text-xs border border-indigo-300 rounded text-indigo-700 hover:bg-indigo-50 whitespace-nowrap">Use next cheque</button>
            )
          )}
        </div>
        {info && <p className="mt-0.5 text-[11px] text-gray-500 truncate" title={info}>{info} · issued in order</p>}
      </div>
    );
  }

  if (hasBooks) {
    return (
      <div>
        <SearchablePagedDropdown
          value={value || ''}
          displayLabel={value || ''}
          onChange={(num) => onChange(num || '')}
          fetchPage={fetchPage}
          resetKey={ledgerAccountId || ''}
          getLabel={(i) => i.chequeNumber}
          getSubLabel={(i) => i.bookNumber}
          noun="cheque"
          searchPlaceholder="Search cheque number..."
          placeholder="Select cheque"
          disabled={disabled}
          error={error}
          menuPlacement={menuPlacement}
        />
        {info && <p className="mt-0.5 text-[11px] text-gray-500 truncate" title={info}>{info}</p>}
      </div>
    );
  }

  return (
    <input
      type="text"
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      placeholder={hasBooks === null && ledgerAccountId ? 'Checking cheque books…' : placeholder}
      className={className || `w-full px-3 py-1.5 text-sm border rounded ${error ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
    />
  );
};

export default ChequeNumberSelect;
